<script setup lang="ts">
import type { BrokerAvatarProps } from '.';

import { AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { cn } from '@/utils';
import {
	getFallbackBrokerImage,
	getFallbackCompanyImage
} from '@/utils/imageUrl';
import { NuxtImg } from '#components';
import { AvatarRoot } from 'reka-ui';

import { avatarVariant, companyAvatarVariant } from '.';

const { ORIGIN } = useRuntimeConfig().public;

const FALLBACK_BROKER_IMAGE = getFallbackBrokerImage(ORIGIN);
const FALLBACK_COMPANY_IMAGE = getFallbackCompanyImage(ORIGIN);

const props = withDefaults(defineProps<BrokerAvatarProps>(), {
	avatarFallbackClass: 'bg-white-dark-3 text-base text-white',
	companyAvatarFallbackClass: 'bg-white-dark-2 text-base text-white',
	showCompanyAvatar: true,
	loading: 'lazy',
	brokerSrc: '',
	companySrc: '',
	initials: '',
	brokerName: '',
	companyName: ''
});

// Compute safe URLs that won't duplicate origin
const safeBrokerSrc = computed(() => props.brokerSrc || FALLBACK_BROKER_IMAGE);
const safeCompanySrc = computed(
	() => props.companySrc || FALLBACK_COMPANY_IMAGE
);

const brokerAlt = computed(() => `${props.brokerName} ${props.companyName}`);
const companyAlt = computed(() => `${props.companyName}`);
</script>

<template>
	<div :class="cn('relative', props.class)">
		<AvatarRoot
			:class="
				cn(
					avatarVariant({ shape: avatarShape, border, isGrayscale }),
					props.class
				)
			"
		>
			<AvatarImage :src="safeBrokerSrc" :alt="brokerAlt" :loading="loading" />
			<AvatarFallback
				as-child
				:class="
					cn(
						'grid size-full place-items-center',
						avatarFallbackClass,
						props.class
					)
				"
			>
				<NuxtImg
					:src="FALLBACK_BROKER_IMAGE"
					width="128"
					height="128"
					:loading="loading"
					format="webp"
					class="size-full rounded-full"
				/>
			</AvatarFallback>
		</AvatarRoot>
		<AvatarRoot
			v-if="showCompanyAvatar"
			:class="cn(companyAvatarVariant({ shape: companyAvatarShape, sizes }))"
		>
			<AvatarImage :src="safeCompanySrc" :alt="companyAlt" :loading="loading" />

			<AvatarFallback
				as-child
				:class="
					cn(
						'grid min-h-full w-full place-items-center',
						companyAvatarFallbackClass
					)
				"
			>
				<NuxtImg
					:src="FALLBACK_COMPANY_IMAGE"
					width="128"
					height="128"
					:loading="loading"
					format="webp"
					class="size-full rounded-full"
				/>
			</AvatarFallback>
		</AvatarRoot>
	</div>
</template>
